import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { courseAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiUpload } from 'react-icons/fi';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Select from 'react-select';

const AddCourse = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();
  const isEditMode = !!id;
  const courseFromState = location.state?.course;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Dropdown options
  const [tradegroups, setTradegroups] = useState([]);
  const [trades, setTrades] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);
  const [teachers, setTeachers] = useState([]);
  
  // Form data - exactly like admin form
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_thumbnail: null,
    tradegroup_id: '',
    trade_id: [],
    course_type: '',
    tag: '',
    validity: '',
    expiry: '',
    teacher: [],
    liveclass: '',
    course_provider: 'youtube',
    course_url: '',
    course_price: '',
    course_discount: '',
    free_course: false
  });
  
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  // Initialize form data immediately if courseFromState exists (prevents data loss)
  useEffect(() => {
    if (isEditMode && courseFromState && !isInitialized) {
      // Set form data immediately to prevent flickering
      setFormData({
        title: courseFromState.title || '',
        description: courseFromState.description || '',
        course_thumbnail: null,
        tradegroup_id: courseFromState.tradegroup_id || '',
        trade_id: courseFromState.trade_ids ? courseFromState.trade_ids.split(',').map(Number) : [],
        course_type: courseFromState.course_type || '',
        tag: courseFromState.tag || '',
        validity: courseFromState.validity || '',
        expiry: courseFromState.expiry || '',
        teacher: courseFromState.teacher_ids ? courseFromState.teacher_ids.split(',').map(Number) : [],
        liveclass: courseFromState.liveclass || '',
        course_provider: courseFromState.course_provider || 'youtube',
        course_url: courseFromState.video_id || '',
        course_price: courseFromState.price || '',
        course_discount: courseFromState.discount || '',
        free_course: courseFromState.free_course == 1
      });
      
      if (courseFromState.course_thumbnail) {
        setThumbnailPreview(courseFromState.course_thumbnail);
      }
    }
  }, []);

  useEffect(() => {
    if (isInitialized) return; // Prevent re-initialization
    
    const loadData = async () => {
      setLoading(true);
      try {
        if (isEditMode) {
          // Load dropdown data first, then populate form
          await fetchDropdownData();
          if (courseFromState) {
            await populateCourseData(courseFromState);
          } else {
            await fetchCourseData();
          }
        } else {
          // Just load dropdowns for add mode
          await fetchDropdownData();
        }
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };
    loadData();
  }, [id]);

  const populateCourseData = async (course) => {
    try {
      // Load trades for the selected tradegroup FIRST
      if (course.tradegroup_id) {
        const tradesRes = await masterAPI.getTradesByGroup(course.tradegroup_id);
        if (tradesRes.data.success) {
          setTrades(tradesRes.data.data);
        }
      }
      
      // THEN pre-fill form with existing course data (after trades are loaded)
      // Use functional update to ensure we don't lose any existing data
      setFormData(prev => ({
        ...prev,
        title: course.title || '',
        description: course.description || '',
        tradegroup_id: course.tradegroup_id || '',
        trade_id: course.trade_ids ? course.trade_ids.split(',').map(Number) : [],
        course_type: course.course_type || '',
        tag: course.tag || '',
        validity: course.validity || '',
        expiry: course.expiry || '',
        teacher: course.teacher_ids ? course.teacher_ids.split(',').map(Number) : [],
        liveclass: course.liveclass || '',
        course_provider: course.course_provider || 'youtube',
        course_url: course.video_id || '',
        course_price: course.price || '',
        course_discount: course.discount || '',
        free_course: course.free_course == 1
      }));
      
      // Set thumbnail preview if exists
      if (course.course_thumbnail) {
        setThumbnailPreview(course.course_thumbnail);
      }
    } catch (error) {
      toast.error('Error loading course data');
      console.error(error);
    }
  };

  const fetchCourseData = async () => {
    try {
      const response = await courseAPI.getCourses();
      if (response.data.success) {
        const course = response.data.data.find(c => c.id === parseInt(id));
        
        if (course) {
          await populateCourseData(course);
        } else {
          toast.error('Course not found');
          navigate('/courses/list');
        }
      }
    } catch (error) {
      toast.error('Error loading course data');
      console.error(error);
      navigate('/courses/list');
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [tradegroupsRes, courseTypesRes, teachersRes] = await Promise.all([
        masterAPI.getTradeGroups(),
        courseAPI.getCourseTypes(),
        courseAPI.getTeachers()
      ]);
      
      if (tradegroupsRes.data.success) {
        setTradegroups(tradegroupsRes.data.data);
      }
      if (courseTypesRes.data.success) {
        setCourseTypes(courseTypesRes.data.data);
      }
      if (teachersRes.data.success) {
        setTeachers(teachersRes.data.data);
      }
    } catch (error) {
      toast.error('Error loading form data');
      console.error(error);
    }
  };

  const handleTradeGroupChange = async (e) => {
    const tradegroupId = e.target.value;
    setFormData({ ...formData, tradegroup_id: tradegroupId, trade_id: [] });
    
    if (tradegroupId) {
      try {
        const response = await masterAPI.getTradesByGroup(tradegroupId);
        if (response.data.success) {
          setTrades(response.data.data);
        }
      } catch (error) {
        toast.error('Error loading trades');
      }
    } else {
      setTrades([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleMultiSelectChange = (e) => {
    const options = e.target.options;
    const values = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        values.push(options[i].value);
      }
    }
    setFormData({ ...formData, [e.target.name]: values });
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        e.target.value = '';
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload a valid image file');
        e.target.value = '';
        return;
      }
      
      setFormData({ ...formData, course_thumbnail: file });
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  // Custom styles for react-select to match Tailwind design
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      padding: '2px 4px',
      borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
      '&:hover': { borderColor: '#9ca3af' },
      borderRadius: '0.5rem',
      minHeight: '42px'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 50
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#dbeafe',
      borderRadius: '0.375rem'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#1e40af',
      fontSize: '0.875rem'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#1e40af',
      ':hover': {
        backgroundColor: '#bfdbfe',
        color: '#1e3a8a'
      }
    })
  };

  // Transform data for react-select format
  const tradegroupOptions = useMemo(() => 
    tradegroups.map(tg => ({ value: tg.id, label: tg.name })),
    [tradegroups]
  );

  const tradeOptions = useMemo(() => 
    trades.map(trade => ({ value: trade.id, label: trade.name })),
    [trades]
  );

  const courseTypeOptions = useMemo(() => 
    courseTypes.map(type => ({ value: type.id, label: type.type })),
    [courseTypes]
  );

  const teacherOptions = useMemo(() => 
    teachers.map(teacher => ({ 
      value: teacher.id, 
      label: `${teacher.name} ${teacher.surname || ''}`.trim()
    })),
    [teachers]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate react-select fields (required attribute doesn't work on react-select)
    if (!formData.tradegroup_id) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.trade_id || formData.trade_id.length === 0) {
      toast.error('Please select at least one subcategory');
      return;
    }
    
    if (!formData.course_type) {
      toast.error('Please select a course type');
      return;
    }
    
    if (!formData.teacher || formData.teacher.length === 0) {
      toast.error('Please assign at least one teacher');
      return;
    }
    
    setSubmitting(true);

    try {
      const data = new FormData();
      
      // Append all form data - exactly like admin
      data.append('title', formData.title);
      data.append('description', formData.description);
      
      // Only append thumbnail if a new file is selected
      if (formData.course_thumbnail) {
        data.append('course_thumbnail', formData.course_thumbnail);
      }
      
      data.append('tradegroup_id', formData.tradegroup_id);
      
      // Append multiple trades
      formData.trade_id.forEach((trade, index) => {
        data.append(`trade_id[${index}]`, trade);
      });
      
      data.append('course_type', formData.course_type);
      data.append('tag', formData.tag);
      data.append('validity', formData.validity);
      data.append('expiry', formData.expiry);
      
      // Append multiple teachers
      formData.teacher.forEach((teacher, index) => {
        data.append(`teacher[${index}]`, teacher);
      });
      
      data.append('liveclass', formData.liveclass);
      data.append('course_provider', formData.course_provider);
      data.append('course_url', formData.course_url);
      data.append('course_price', formData.course_price);
      data.append('course_discount', formData.course_discount || 0);
      data.append('free_course', formData.free_course ? 1 : 0);

      // Call appropriate API based on mode
      const response = isEditMode 
        ? await courseAPI.updateCourse(id, data)
        : await courseAPI.createCourse(data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/courses/list');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Error ${isEditMode ? 'updating' : 'creating'} course`);
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/courses/list')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <FiArrowLeft /> Back to Courses
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Course' : 'Add Course'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-300 rounded-lg">
                <CKEditor
                  key={`ckeditor-${isEditMode ? id : 'new'}`}
                  editor={ClassicEditor}
                  data={formData.description}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData(prev => ({ ...prev, description: data }));
                  }}
                  onReady={(editor) => {
                    // Ensure data is set after editor is ready
                    if (formData.description && editor.getData() !== formData.description) {
                      editor.setData(formData.description);
                    }
                  }}
                  config={{
                    placeholder: 'Enter course description...',
                    toolbar: [
                      'heading', '|',
                      'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                      'blockQuote', 'insertTable', '|',
                      'undo', 'redo'
                    ]
                  }}
                />
              </div>
            </div>

            {thumbnailPreview && (
              <div>
                <img src={thumbnailPreview} alt="Preview" className="w-48 h-auto rounded-lg border-2 border-gray-200" />
              </div>
            )}
          </div>

          {/* Right Column - Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Inline Preview Image {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-300">
                <FiUpload />
                {isEditMode ? 'Change Image (Optional)' : 'Choose File'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="hidden"
                  required={!isEditMode}
                />
              </label>
              {isEditMode && !formData.course_thumbnail && thumbnailPreview && (
                <p className="text-xs text-gray-500 mt-1">Current image will be kept if no new file is selected</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <Select
                name="tradegroup_id"
                value={tradegroupOptions.find(opt => opt.value === formData.tradegroup_id) || null}
                onChange={(selectedOption) => {
                  const event = {
                    target: {
                      value: selectedOption?.value || '',
                      name: 'tradegroup_id'
                    }
                  };
                  handleTradeGroupChange(event);
                }}
                options={tradegroupOptions}
                placeholder="Select Category"
                isClearable
                isSearchable
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sub Category <span className="text-red-500">*</span>
              </label>
              <Select
                name="trade_id"
                value={tradeOptions.filter(opt => formData.trade_id.includes(opt.value))}
                onChange={(selectedOptions) => {
                  const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                  setFormData({ ...formData, trade_id: values });
                }}
                options={tradeOptions}
                placeholder="Select Subcategories"
                isMulti
                isClearable
                isSearchable
                isDisabled={!formData.tradegroup_id}
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Type <span className="text-red-500">*</span>
              </label>
              <Select
                name="course_type"
                value={courseTypeOptions.find(opt => opt.value === formData.course_type) || null}
                onChange={(selectedOption) => {
                  setFormData({ ...formData, course_type: selectedOption?.value || '' });
                }}
                options={courseTypeOptions}
                placeholder="Select Course Type"
                isClearable
                isSearchable
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tag <span className="text-red-500">*</span>
              </label>
              <select
                name="tag"
                value={formData.tag}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                required
              >
                <option value="">Select Tag</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Validity (In Month) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="validity"
                value={formData.validity}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                (Or) Set Expiry
              </label>
              <input
                type="date"
                name="expiry"
                value={formData.expiry}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign Teacher <span className="text-red-500">*</span>
              </label>
              <Select
                name="teacher"
                value={teacherOptions.filter(opt => formData.teacher.includes(opt.value))}
                onChange={(selectedOptions) => {
                  const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                  setFormData({ ...formData, teacher: values });
                }}
                options={teacherOptions}
                placeholder="Select Teachers"
                isMulti
                isClearable
                isSearchable
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Live Classes <span className="text-red-500">*</span>
              </label>
              <select
                name="liveclass"
                value={formData.liveclass}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                required
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Preview URL
              </label>
              <div className="flex gap-2 mb-2">
                <select
                  name="course_provider"
                  value={formData.course_provider}
                  onChange={handleInputChange}
                  className="w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                  required
                >
                  <option value="youtube">Youtube</option>
                  <option value="html5">Html5</option>
                </select>
              
                <input
                  type="text"
                  name="course_url"
                  value={formData.video_id}
                  onChange={handleInputChange}
                  placeholder="Enter URL"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                  
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="course_price"
                  value={formData.course_price}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount (%)
                </label>
                <input
                  type="number"
                  name="course_discount"
                  value={formData.course_discount}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Free Course
                </label>
                <div className="flex items-center h-10">
                  <input
                    type="checkbox"
                    name="free_course"
                    checked={formData.free_course}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-600"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/courses/list')}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            disabled={submitting}
          >
            {submitting 
              ? (isEditMode ? 'Updating Course...' : 'Adding Course...')
              : (isEditMode ? 'Update Course' : 'Add Course')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCourse;

